// StudentFeesPayment.jsx
import React, { useEffect, useState, useRef } from "react";
import Sidebar from "../components/Sidebar";
import axios from "axios";
import "../assets/css/payments.css";
import "../assets/css/payschfees.css";

export default function StudentFeesPayment() {
  const [userData, setUserData] = useState(null);
  const [invoice, setInvoice] = useState(null); // { feeAmount, totalPaid, balance }
  const [rrr, setRRR] = useState(null);
  const [currentPaymentId, setCurrentPaymentId] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false); // confirm full payment (no RRR exists)
  const [showExistingRRRModal, setShowExistingRRRModal] = useState(false); // when RRR already exists
  const [iframeOpen, setIframeOpen] = useState(false);
  const [loading, setLoading] = useState(true); // initial page load
  const [createLoading, setCreateLoading] = useState(false); // RRR creation loading
  const [message, setMessage] = useState("");
  const [verifying, setVerifying] = useState(false);

  const API = process.env.REACT_APP_API_BASE_URL || "http://localhost:3003";
  const matricNo = localStorage.getItem("matricNo");
  const pollRef = useRef(null);

  // load biodata + invoice
  useEffect(() => {
    async function init() {
      try {
        setLoading(true);
        setMessage("");
        // fetch biodata
        const { data: bio } = await axios.get(`${API}/api/biodata/${matricNo}`);
        if (!bio || !bio.matricNumber) {
          setMessage("Biodata not found for this student. Contact support.");
          setUserData(null);
          setInvoice(null);
          return;
        }
        setUserData({
          fullName: bio.fullName,
          email: bio.email,
          phoneNumber: bio.phoneNumber,
          programmeName: bio.department || bio.programme || "",
          level: bio.Level ?? bio.level,
          session: bio.session,
        });

        // fetch invoice/payment info from server
        const { data: payInfo } = await axios.get(`${API}/api/paystudentfees`, {
          params: { matricNo },
        });

        // handle server response shapes defensively
        if (!payInfo || typeof payInfo !== "object") {
          setMessage("Unable to load payment info. Contact finance.");
          setInvoice(null);
          setRRR(null);
        } else {
          setRRR(payInfo?.RRR ?? null);

          if (payInfo.feeAmount == null || payInfo.balance == null) {
            setInvoice(null);
            setMessage(
              payInfo.warning ||
                "Invoice not configured for your programme/level/session.",
            );
          } else {
            setInvoice({
              feeAmount: Number(payInfo.feeAmount || 0),
              totalPaid: Number(payInfo.totalPaid || 0),
              balance: Number(payInfo.balance || 0),
            });

            if (payInfo.Status && payInfo.Status.toLowerCase() === "paid") {
              setMessage("Your payment is complete. Thank you!");
            } else {
              setMessage("");
            }
          }
        }
      } catch (err) {
        console.error("init error:", err?.response?.data || err.message || err);
        setMessage(
          "Unable to load payment information. Try again or contact finance.",
        );
        setInvoice(null);
        setRRR(null);
      } finally {
        setLoading(false);
      }
    }
    init();

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When Pay button clicked — show appropriate modal
  const onPayButtonClick = () => {
    if (!invoice) {
      setMessage("Invoice not loaded or not configured.");
      return;
    }
    if (rrr) {
      // show modal telling student to pay existing RRR
      setShowExistingRRRModal(true);
      setMessage("");
      return;
    }
    // otherwise, show confirmation modal to generate RRR for full outstanding
    setShowConfirmModal(true);
    setMessage("");
  };

  async function fetchLatestPendingRRR() {
    try {
      const { data } = await axios.get(`${API}/api/paystudentfees`, {
        params: { matricNo },
      });
      return data?.RRR ?? null;
    } catch (err) {
      console.error(
        "fetchLatestPendingRRR error:",
        err?.response?.data || err.message || err,
      );
      return null;
    }
  }

  // Create RRR — force full outstanding (no partials)
  const createRRR = async () => {
    if (!userData) return setMessage("User data missing.");
    if (!invoice) return setMessage("Invoice not loaded.");
    // amount determined by server-side computed outstanding (invoice.balance)
    const amount = Number(invoice.balance || 0);
    if (!amount || amount <= 0)
      return setMessage("No outstanding balance to pay.");

    // guard again: do not allow creation if RRR exists
    if (rrr) {
      setMessage(
        "A Remita number already exists for your payment. You cannot create another one.",
      );
      // close confirm modal and open existing RRR modal so user can proceed to pay
      setShowConfirmModal(false);
      setShowExistingRRRModal(true);
      return;
    }

    try {
      setCreateLoading(true);
      setMessage("Creating RRR for full outstanding amount...");

      const payload = {
        matricNo,
        programmeName: userData.programmeName,
        level: userData.level,
        session: userData.session,
        payerName: userData.fullName,
        payerEmail: userData.email,
        payerPhone: userData.phoneNumber,
        amount, // send full outstanding
      };

      console.debug("createRRR payload:", payload);

      const { data } = await axios.post(`${API}/api/paystudentfees`, payload);
      console.debug("createRRR response:", data);

      if (data?.RRR) {
        // successful — use returned RRR
        setRRR(data.RRR);
        setCurrentPaymentId(data.paymentId ?? null);
        setShowConfirmModal(false);
        setIframeOpen(true);
        setMessage("RRR created. Proceed to payment (iframe opened).");
        startPoll(data.RRR);
        return;
      }

      // server indicates existing pending — do not create another
      if (data?.msg && data.msg.toLowerCase().includes("existing pending")) {
        setMessage(
          "An existing pending payment was found. You cannot create another RRR. Checking for RRR...",
        );
        const latestRRR = await fetchLatestPendingRRR();
        if (latestRRR) {
          setRRR(latestRRR);
          setShowConfirmModal(false);
          setShowExistingRRRModal(true);
          setIframeOpen(true);
          startPoll(latestRRR);
          setMessage("Existing RRR found — proceed to payment.");
          return;
        }
        setShowConfirmModal(false);
        return setMessage(
          "A pending payment exists (no RRR yet). Contact finance to resolve.",
        );
      }

      if (data?.msg) {
        setMessage(data.msg);
        return;
      }

      setMessage("Unexpected response from server. See console for details.");
      console.warn("Unexpected createRRR response:", data);
    } catch (err) {
      console.error(
        "createRRR error:",
        err?.response?.data || err.message || err,
      );
      if (err.response) {
        const serverMsg =
          err.response.data?.msg || JSON.stringify(err.response.data);
        setMessage(`Server error: ${serverMsg}`);
      } else {
        setMessage(`Network error: ${err.message || err}`);
      }
    } finally {
      setCreateLoading(false);
    }
  };

  const startPoll = (rrrToCheck) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        setVerifying(true);
        const resp = await axios.get(`${API}/api/payments/verify`, {
          params: { rrr: rrrToCheck },
        });
        console.debug("poll verify resp:", resp.data);
        if (resp.data?.msg && resp.data.msg.toLowerCase() === "paid") {
          // update invoice on paid
          setInvoice({
            feeAmount: Number(resp.data.feeAmount || 0),
            totalPaid: Number(resp.data.totalPaid || 0),
            balance: Number(resp.data.balance || 0),
          });
          setMessage("Payment confirmed. Thank you!");
          setIframeOpen(false);
          // remove the current rrr so student can perform new action if needed
          setRRR(null);
          if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        }
      } catch (err) {
        console.error(
          "poll verify error:",
          err?.response?.data || err.message || err,
        );
      } finally {
        setVerifying(false);
      }
    }, 5000);
  };

  const verifyNow = async () => {
    if (!rrr) return setMessage("No RRR to verify.");
    try {
      setVerifying(true);
      setMessage("Verifying payment...");
      const resp = await axios.get(`${API}/api/payments/verify`, {
        params: { rrr },
      });
      console.debug("verifyNow resp:", resp.data);
      if (resp.data?.msg && resp.data.msg.toLowerCase() === "paid") {
        setInvoice({
          feeAmount: Number(resp.data.feeAmount || 0),
          totalPaid: Number(resp.data.totalPaid || 0),
          balance: Number(resp.data.balance || 0),
        });
        setMessage("Payment confirmed. Thank you!");
        setIframeOpen(false);
        setRRR(null);
        if (pollRef.current) clearInterval(pollRef.current);
      } else {
        setMessage(
          "Payment not yet completed — please wait or try Verify again shortly. If you have paid and it's persist please contact school admin with your matric number and rrr.  ",
        );
      }
    } catch (err) {
      console.error(
        "verifyNow error:",
        err?.response?.data || err.message || err,
      );
      setMessage("Failed to verify payment. See console for details.");
    } finally {
      setVerifying(false);
    }
  };

  // copy RRR to clipboard helper
  const copyRRR = async () => {
    if (!rrr) return;
    try {
      await navigator.clipboard.writeText(rrr);
      setMessage("RRR copied to clipboard.");
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      console.error("copy rrr failed", err);
      setMessage("Unable to copy RRR. Please copy manually.");
    }
  };

  if (loading) return <p>Loading…</p>;

  const invoiceLoaded = invoice !== null;
  const balance = invoiceLoaded ? Number(invoice.balance) : null;
  const total = invoiceLoaded ? Number(invoice.feeAmount) : null;
  const totalPaid = invoiceLoaded ? Number(invoice.totalPaid) : 0;
  const alreadyPaid = invoiceLoaded ? balance <= 0 : false;

  return (
    <div className="result-page" style={{ marginTop: "-130px" }}>
      <Sidebar />
      <div className="result-main">
        <main className="results-view-page">
          <h1>Pay School Fees</h1>

          {invoiceLoaded ? (
            <div style={{ marginBottom: 12 }}>
              <div>
                Total due: <strong>₦{Number(total).toFixed(2)}</strong>
              </div>

              <div
                style={{
                  marginTop: 8,
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <span className="badge paid">
                  Paid: ₦{Number(totalPaid).toFixed(2)}
                </span>
                <span className="badge outstanding">
                  Outstanding: ₦{Number(balance).toFixed(2)}
                </span>
                {rrr && <span className="badge rrr">RRR: {rrr}</span>}
              </div>
            </div>
          ) : (
            <div className="payment-warning">
              {message ||
                "Invoice not configured for your programme/level/session."}
            </div>
          )}

          {invoiceLoaded && alreadyPaid ? (
            <p className="message">
              <strong>Fees Paid</strong> – Thank you!
            </p>
          ) : invoiceLoaded ? (
            <>
              <button
                className="btn"
                onClick={onPayButtonClick}
                disabled={createLoading || verifying}
                title={
                  rrr
                    ? "You already have an RRR — click to see instructions to pay it."
                    : ""
                }
              >
                Pay Full Amount
              </button>

              {rrr && (
                <div className="rrr-box" style={{ marginTop: 12 }}>
                  <p>
                    Your RRR: <strong>{rrr}</strong>
                  </p>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      className="btn"
                      onClick={() => {
                        setIframeOpen(true);
                        startPoll(rrr);
                      }}
                      disabled={verifying}
                    >
                      Proceed to Payment
                    </button>
                    <button
                      className="btn"
                      onClick={verifyNow}
                      disabled={verifying}
                      style={{ marginLeft: 8 }}
                    >
                      Verify Payment
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : null}

          {message && <p className="message">{message}</p>}

          {/* Confirmation modal: pay full outstanding (no edits allowed) */}
          {showConfirmModal && invoiceLoaded && (
            <div
              className="modal-overlay"
              onClick={() => setShowConfirmModal(false)}
            >
              <div
                className="modal-content"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="modal-close"
                  onClick={() => setShowConfirmModal(false)}
                >
                  ×
                </button>
                <h3>Confirm Full Payment</h3>
                <p>
                  You are about to pay the full outstanding amount for this
                  session.
                </p>
                <p style={{ fontWeight: 700, marginTop: 8 }}>
                  Amount to pay: ₦{Number(balance).toFixed(2)}
                </p>
                <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
                  <button
                    className="btn"
                    onClick={createRRR}
                    disabled={createLoading || !!rrr}
                  >
                    {createLoading
                      ? "Creating..."
                      : "Create Payment (RRR) & Proceed"}
                  </button>
                  <button
                    className="btn"
                    onClick={() => setShowConfirmModal(false)}
                    disabled={createLoading}
                  >
                    Cancel
                  </button>
                </div>
                {rrr && (
                  <p className="message">
                    A Remita number already exists — you cannot create another.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Existing RRR modal */}
          {showExistingRRRModal && rrr && (
            <div
              className="modal-overlay"
              onClick={() => setShowExistingRRRModal(false)}
            >
              <div
                className="modal-content"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  className="modal-close"
                  onClick={() => setShowExistingRRRModal(false)}
                >
                  ×
                </button>
                <h3>Existing Payment Found</h3>
                <p>
                  You already have a Remita Reference Number (RRR) for this
                  payment. Please use the existing RRR to complete payment.
                </p>
                <p style={{ marginTop: 8 }}>
                  <strong>RRR:</strong>{" "}
                  <span style={{ fontFamily: "monospace" }}>{rrr}</span>
                </p>
                <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
                  <button
                    className="btn"
                    onClick={() => {
                      setIframeOpen(true);
                      startPoll(rrr);
                      setShowExistingRRRModal(false);
                    }}
                  >
                    Proceed to Payment
                  </button>
                  <button className="btn" onClick={copyRRR}>
                    Copy RRR
                  </button>
                  <button
                    className="btn"
                    onClick={() => setShowExistingRRRModal(false)}
                  >
                    Close
                  </button>
                </div>
                <p style={{ marginTop: 10, fontSize: 13, color: "#666" }}>
                  If you believe this is an error, contact finance with your
                  matric number.
                </p>
              </div>
            </div>
          )}

          {/* iframe */}
          {iframeOpen && rrr && (
            <div className="modal-overlay" onClick={() => setIframeOpen(false)}>
              <div
                className="modal-content"
                onClick={(e) => e.stopPropagation()}
                style={{ width: "90%", height: "90%" }}
              >
                <button
                  className="modal-close"
                  onClick={() => setIframeOpen(false)}
                >
                  ×
                </button>
                <iframe
                  title="Remita Payment"
                  src={`https://luth-school-payment.netlify.app/?code=${encodeURIComponent(rrr)}`}
                  style={{ width: "100%", height: "100%", border: "none" }}
                />
                <div style={{ marginTop: 8 }}>
                  <button
                    className="btn"
                    onClick={verifyNow}
                    disabled={verifying}
                  >
                    Verify Payment Now
                  </button>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
